// 騎士の歩き (踏みしめ歩き・開いて出て寄せる) の床の砂 — 見本の絵から再現する (2026-10-01・票 knight-fx-motion-1001)。
//   検収 10-01d (ユーザー S-1「C 寄せ足の擦り」・メモ「Cでもう少し抑えめでいいと思う」) → C を既定 (配布版) にし、
//   比較版より薄く・短く・粒を少なくした (PLAN.c)。DEV `?knightStepDust=a|b|d` で なし/他の案と見比べられる。
//   ユーザー (09-30c S-2)「砂に見えない」「前足の後ろから出るのでは？」→ 台帳 production.md「FXの作り方」の追加手順
//   (場面を画像生成して像を先に確かめる) → 見本 そざい/fx/knight-fx-look-1001/compare_dust.png
//   (A なし / B 着地の舞い / C 寄せ足の擦り / D 蹴り出し) → 10-01「動きを見ないとわからないけどいったんりょうほうBかな？」。
//   材料 = public/sprites/fx/knight-step-dust/{b,c,d}_{1,2,3}.png (scripts/knight_step_dust_material.py が見本と「砂なし」A の
//     差から切り出した砂の濃さ。白・透明度 = 濃さ)。各コマの出始め/最大/散り際が 1/2/3。
//   動き (プログラムが持つ): 各コマを「砂の出どころからの距離」で5つの塊に分け、塊ごとに時間差で出どころから広がり、
//     次のコマへ受け渡しながら、上から先にまだらにほどけて消える。見本の小さな粒のかけらを数粒、低く跳ねさせる。
//     1回ごとに大きさ・塊の時間差・ほどけ方の種を変える (毎歩同じ形の繰り返しにしない)。
//   出どころ: B = 着いた前足 (1歩を運び終えた時) / C = 寄せ終わった後ろ足 (同じ時) / D = 蹴り出す後ろ足 (1歩の出だし)。
//   描く順 = 騎士の絵の後 (見本どおり、舞った砂が足先に少し被さる。砂は低いので脛より上は覆わない)。
// ブラウザ専用の描画 (Image が無い Vitest では描かない)。出す場所の計算 knightStepDustEventsFor はテストできる。
import type { Boss } from './types'
import { getKnightMove, getKnightWalkPose, knightGaitDrawOffsetPx, knightWalkFootPx } from './knight_gait'

export type KnightStepDustOption = 'a' | 'b' | 'c' | 'd'
let option: KnightStepDustOption = 'c'
export function getKnightStepDust(): KnightStepDustOption { return option }
export function setKnightStepDust(v: string | null): void { option = v === 'a' || v === 'b' || v === 'd' ? v : 'c' }

const BASE = '/sprites/fx/knight-step-dust/'
/** 材料の「砂の出どころ」と床 (材料の px)。scripts/knight_step_dust_material.py の meta.json。 */
const FRAME_ORIGIN: Record<string, { ox: number; oy: number }> = {
  b_1: { ox: 64.2, oy: 21.6 }, b_2: { ox: 130.8, oy: 57.6 }, b_3: { ox: 126.6, oy: 25.8 },
  c_2: { ox: 63.0, oy: 30.6 }, c_3: { ox: 68.4, oy: 21.0 },
  d_1: { ox: -16.2, oy: 70.8 }, d_2: { ox: 81.0, oy: 84.0 }, d_3: { ox: 34.2, oy: 31.8 },
}
/** 見本の砂の色 (見本の濃い所から床を引いた色の中央値)。 */
const TINT = [163, 153, 144] as const
/** 材料 px → ゲーム px。縦 = 騎士の背丈の比 (ゲーム 216px / 見本 417px) ÷ 材料の縮尺 0.6。 */
const MAT_TO_GAME_Y = 216 / 417 / 0.6
/** 横は案ごと。見本の騎士は歩幅がゲームより広い (広い構えの両足 見本 238px・ゲーム 77px)。
 *  足の所で跳ねる砂 (B・D) は背丈の比、両足の間を擦る砂 (C) は歩幅の比に寄せる。 */
const MAT_TO_GAME_X: Record<'b' | 'c' | 'd', number> = { b: MAT_TO_GAME_Y, c: (77 / 238) / 0.6, d: MAT_TO_GAME_Y * 0.85 }
const CHUNKS = 5
/** 見本の濃さはゲームの暗い床で薄く見える (09-30c「砂に見えない」の一因)。材料の濃さを持ち上げる。 */
const ALPHA_GAIN = 1.6
const ERODE_LEVELS = [0, 0.35, 0.62, 0.88]
const SEEDS = 2

interface Layer { frame: string; at: number; scale?: number }
interface Plan { layers: Layer[]; end: number; lag: number; alpha: number; grit: { n: [number, number]; vx: [number, number]; vy: [number, number] } }
/** 時間割 (ms・出来事からの経過)。at = そのコマがいちばん濃い時。最後のコマは end までにほどけ切る。
 *  grit.vx の + = 進む向き (C・B) / D は − = 後ろへ蹴る。 */
const PLAN: Record<'b' | 'c' | 'd', Plan> = {
  b: { layers: [{ frame: 'b_1', at: 45 }, { frame: 'b_2', at: 200 }, { frame: 'b_3', at: 500 }], end: 980, lag: 60, alpha: 1,
    grit: { n: [5, 8], vx: [-40, 150], vy: [60, 150] } },
  // 10-01d「もう少し抑えめ」: 濃さ 1→0.75・大きさ ×0.85 (scale)・消えるまで 860→600ms
  //   (歩きの1歩 約 520ms とほぼ同じ = 前の砂が消えかけた頃に次が出る・途切れず続く帯にしない)・粒 2〜4→1〜2 (低く短く)。
  c: { layers: [{ frame: 'c_2', at: 50, scale: 0.47 }, { frame: 'c_2', at: 140, scale: 0.85 }, { frame: 'c_3', at: 320, scale: 0.85 }], end: 600, lag: 40, alpha: 0.75,
    grit: { n: [1, 2], vx: [-15, 50], vy: [25, 60] } },
  d: { layers: [{ frame: 'd_1', at: 70 }, { frame: 'd_2', at: 260 }, { frame: 'd_3', at: 520 }], end: 900, lag: 60, alpha: 1,
    grit: { n: [4, 7], vx: [-190, -60], vy: [70, 170] } },
}

const hash01 = (n: number): number => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }
function noise2(x: number, y: number, seed: number, cell: number): number {
  const cx = Math.floor(x / cell), cy = Math.floor(y / cell)
  const fx = x / cell - cx, fy = y / cell - cy
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy)
  const v = (i: number, j: number) => hash01((cx + i) * 57.3 + (cy + j) * 91.1 + seed * 13.7)
  const a = v(0, 0) + (v(1, 0) - v(0, 0)) * sx
  const b = v(0, 1) + (v(1, 1) - v(0, 1)) * sx
  return a + (b - a) * sy
}
const smooth = (p: number): number => { const c = Math.max(0, Math.min(1, p)); return c * c * (3 - 2 * c) }

/** 1つのコマ: 塊 [chunk][seed][erode] と、塊の出どころからの距離 (0..1)。 */
interface Baked { chunks: HTMLCanvasElement[][][]; dist: number[]; w: number; h: number }
const baked = new Map<string, Baked>()
/** 粒のかけら (見本の最大コマの明るい粒を小さく切り出したもの)。 */
const gritBits = new Map<string, HTMLCanvasElement[]>()
const loading = new Set<string>()

function bakeFrame(name: string, img: HTMLImageElement): void {
  const w = img.width, h = img.height
  const src = document.createElement('canvas')
  src.width = w; src.height = h
  const g0 = src.getContext('2d')!
  g0.drawImage(img, 0, 0)
  const base = g0.getImageData(0, 0, w, h).data
  const o = FRAME_ORIGIN[name]!
  // 塊の分け方 = 出どころからの横の距離を、揺らいだ境目で5つへ (合わせると元のコマ)。
  let maxD = 1
  for (let x = 0; x < w; x++) maxD = Math.max(maxD, Math.abs(x - o.ox))
  const weights = (x: number, y: number): number[] => {
    const d = Math.abs(x - o.ox) / maxD + 0.12 * (noise2(x, y, 11, 14) - 0.5)
    const pos = Math.max(0, Math.min(CHUNKS - 1, d * CHUNKS - 0.5))
    const out = new Array(CHUNKS).fill(0)
    const lo = Math.floor(pos), f = pos - lo
    out[lo] = 1 - f
    if (lo + 1 < CHUNKS) out[lo + 1] = f
    return out
  }
  const chunks: HTMLCanvasElement[][][] = []
  const dist: number[] = []
  for (let c = 0; c < CHUNKS; c++) {
    dist.push((c + 0.5) / CHUNKS)
    const bySeed: HTMLCanvasElement[][] = []
    for (let seed = 0; seed < SEEDS; seed++) {
      const levels: HTMLCanvasElement[] = []
      for (const erode of ERODE_LEVELS) {
        const cv = document.createElement('canvas')
        cv.width = w; cv.height = h
        const g = cv.getContext('2d')!
        const d = g.createImageData(w, h)
        const p = d.data
        for (let y = 0; y < h; y++) {
          // 上ほど先に消える (床すれすれが最後まで残る)。
          const height01 = Math.max(0, Math.min(1, (o.oy - y) / Math.max(1, o.oy)))
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4
            let a = Math.min(1, (base[i + 3]! / 255) * ALPHA_GAIN)
            if (a <= 0) continue
            a *= weights(x, y)[c]!
            if (a <= 0.002) continue
            if (erode > 0) {
              const n = 0.6 * noise2(x, y, 3 + seed * 17 + c, 9) + 0.4 * noise2(x * 2.3, y * 2.3, 7 + seed * 5 + c, 6)
              const thr = erode * (0.3 + 0.9 * n) * (0.45 + 1.1 * height01)
              a *= Math.max(0, Math.min(1, (a - thr + 0.2) / 0.4))
            }
            p[i] = TINT[0]; p[i + 1] = TINT[1]; p[i + 2] = TINT[2]
            p[i + 3] = Math.round(Math.min(1, a) * 255)
          }
        }
        g.putImageData(d, 0, 0)
        levels.push(cv)
      }
      bySeed.push(levels)
    }
    chunks.push(bySeed)
  }
  baked.set(name, { chunks, dist, w, h })
  // 粒のかけら: 最大コマの明るい点を 7px 角で切り出す (縁は丸くぼかす)。
  if (name.endsWith('_2')) {
    const pts: { x: number; y: number; a: number }[] = []
    for (let y = 3; y < h - 3; y += 2) for (let x = 3; x < w - 3; x += 2) {
      const a = base[(y * w + x) * 4 + 3]!
      if (a > 150) pts.push({ x, y, a: a + hash01(x * 7 + y) * 40 })
    }
    pts.sort((p, q) => q.a - p.a)
    const bits: HTMLCanvasElement[] = []
    for (const pt of pts) {
      if (bits.length >= 8) break
      if (bits.length && hash01(pt.x * 3.1 + pt.y) < 0.6) continue
      const cv = document.createElement('canvas')
      cv.width = 7; cv.height = 7
      const g = cv.getContext('2d')!
      const d = g.createImageData(7, 7)
      for (let yy = 0; yy < 7; yy++) for (let xx = 0; xx < 7; xx++) {
        const sa = base[((pt.y + yy - 3) * w + (pt.x + xx - 3)) * 4 + 3]! / 255
        const r = Math.hypot(xx - 3, yy - 3) / 3.5
        const i = (yy * 7 + xx) * 4
        d.data[i] = Math.min(255, TINT[0] + 25); d.data[i + 1] = Math.min(255, TINT[1] + 25); d.data[i + 2] = Math.min(255, TINT[2] + 25)
        d.data[i + 3] = Math.round(255 * sa * Math.max(0, 1 - r * r))
      }
      g.putImageData(d, 0, 0)
      bits.push(cv)
    }
    gritBits.set(name[0]!, bits)
  }
}

function framesOf(opt: 'b' | 'c' | 'd'): string[] { return [...new Set(PLAN[opt].layers.map((l) => l.frame))] }

/** 材料の読み込み開始 (選んだ案だけ)。 */
export function preloadKnightStepDust(): void {
  if (option === 'a' || typeof Image === 'undefined' || typeof document === 'undefined') return
  for (const name of framesOf(option)) {
    if (loading.has(name)) continue
    loading.add(name)
    const img = new Image()
    img.onload = () => bakeFrame(name, img)
    img.src = `${BASE}${name}.png`
  }
}

export interface KnightStepDustEvent {
  opt: 'b' | 'c' | 'd'
  /** 砂の出どころ (世界の x) と床 (世界の y)。 */
  x: number; y: number
  /** 見本の「前」(材料の −x) を向ける世界の向き = 歩いて進む向き。 */
  travel: 1 | -1
  born: number
  /** 1回ごとの揺らぎ。 */
  sx: number; sy: number; seed: number; jitter: number[]; weight: number[]
  grit: { t0: number; x: number; vx: number; vy: number; size: number; bit: number }[]
}

function makeEvent(opt: 'b' | 'c' | 'd', x: number, y: number, travel: 1 | -1, born: number): KnightStepDustEvent {
  const r = (k: number) => hash01(born * 0.731 + x * 0.113 + k * 17.9)
  const plan = PLAN[opt]
  const n = plan.grit.n[0] + Math.floor(r(1) * (plan.grit.n[1] - plan.grit.n[0] + 1))
  return {
    opt, x, y, travel, born,
    sx: 0.88 + 0.24 * r(2), sy: 0.86 + 0.24 * r(3), seed: r(4) < 0.5 ? 0 : 1,
    jitter: Array.from({ length: CHUNKS }, (_, i) => (r(10 + i) - 0.5) * 70),
    weight: Array.from({ length: CHUNKS }, (_, i) => 0.78 + 0.34 * r(20 + i)),
    grit: Array.from({ length: n }, (_, i) => ({
      t0: (opt === 'd' ? 10 : 25) + r(30 + i) * 70,
      x: (r(40 + i) - 0.5) * 8,
      vx: plan.grit.vx[0] + (plan.grit.vx[1] - plan.grit.vx[0]) * r(50 + i),
      vy: plan.grit.vy[0] + (plan.grit.vy[1] - plan.grit.vy[0]) * r(60 + i),
      size: 0.45 + 0.5 * r(70 + i),
      bit: Math.floor(r(80 + i) * 8),
    })),
  }
}

const GRIT_G = 1100
const GRIT_FADE_MS = 160

function drawEvent(ctx: CanvasRenderingContext2D, e: KnightStepDustEvent, now: number): void {
  const age = now - e.born
  const plan = PLAN[e.opt]
  if (age < 0 || age > plan.end + 120) return
  const kx = MAT_TO_GAME_X[e.opt] * e.sx, ky = MAT_TO_GAME_Y * e.sy
  const L = plan.layers
  for (let li = 0; li < L.length; li++) {
    const layer = L[li]!
    const b = baked.get(layer.frame)
    if (!b) continue
    const o = FRAME_ORIGIN[layer.frame]!
    const start = li === 0 ? 0 : L[li - 1]!.at
    const next = li + 1 < L.length ? L[li + 1]!.at : plan.end
    const last = li === L.length - 1
    for (let c = 0; c < CHUNKS; c++) {
      // 出どころから遠い塊ほど遅れて出て遅れて消える。塊ごとに少しずつずらす (同時に消えない)。
      const t = age - plan.lag * b.dist[c]! - e.jitter[c]! * (li === 0 ? 0.3 : 1)
      const rise = smooth((t - start) / Math.max(1, layer.at - start))
      if (rise <= 0) continue
      const fall = smooth((t - layer.at) / Math.max(1, next - layer.at))
      if (fall >= 1) continue
      // ほどけ段: 次のコマへ受け渡す間は途中まで、最後のコマは床すれすれまでほどける。
      const erodeTo = last ? ERODE_LEVELS.length - 1 : 2
      const ev = fall * erodeTo
      const lo = Math.floor(ev), hi = Math.min(ERODE_LEVELS.length - 1, lo + 1), w = ev - lo
      // 散り際のコマは見本でもいちばん薄い (床へ沈むもや)。濃さの持ち上げを少し戻す。
      const alpha = plan.alpha * e.weight[c]! * rise * (last ? 0.9 * Math.pow(1 - fall, 1.1) : 1 - 0.55 * fall)
      if (alpha <= 0.004) continue
      // 広がり: 出どころを中心に、出始めは小さく低く、最大へ向けて広がる。最後のコマは床へ沈む。
      const grow = (layer.scale ?? 1) * (0.62 + 0.38 * rise)
      const drift = (last ? 6 : 3) * fall * b.dist[c]!
      const sink = last ? (1 - 0.2 * rise) * (1 - 0.4 * fall) : 1
      ctx.save()
      ctx.translate(e.x, e.y)
      ctx.scale(-e.travel * kx * grow, ky * grow * sink)
      // 流れ = 材料の x で B・C は前 (−)、D は後ろ (+)。ゲームの px を材料の px へ直す。
      const dx = -o.ox + (e.opt === 'd' ? 1 : -1) * drift / (kx * grow)
      const levels = b.chunks[c]![e.seed]!
      ctx.globalAlpha = Math.min(1, alpha * (1 - w))
      ctx.drawImage(levels[lo]!, dx, -o.oy)
      if (hi !== lo && w > 0.01) {
        ctx.globalAlpha = Math.min(1, alpha * w)
        ctx.drawImage(levels[hi]!, dx, -o.oy)
      }
      ctx.restore()
    }
  }
  // 粒: 出どころから低く跳ね、床に落ちたら少し滑って消える。
  const bits = gritBits.get(e.opt)
  if (bits && bits.length) {
    for (const p of e.grit) {
      const t = (age - p.t0) / 1000
      if (t < 0) continue
      const tLand = (2 * p.vy) / GRIT_G
      const tt = Math.min(t, tLand)
      const slide = Math.max(0, t - tLand)
      const x = e.x + e.travel * (p.x + p.vx * tt + p.vx * 0.25 * Math.min(slide, 0.08))
      const y = e.y - (p.vy * tt - 0.5 * GRIT_G * tt * tt) - 1
      const a = slide <= 0 ? 1 : Math.max(0, 1 - (slide * 1000) / GRIT_FADE_MS)
      if (a <= 0.01) continue
      const bit = bits[p.bit % bits.length]!
      const s = p.size
      ctx.globalAlpha = 0.9 * a
      ctx.drawImage(bit, x - 3.5 * s, y - 3.5 * s, 7 * s, 7 * s)
    }
  }
  ctx.globalAlpha = 1
}

/** 歩きの1 tick の前後から、出す砂を決める (案 a・踏みしめ歩きの「開いて出て寄せる」以外・待機以外は何も出さない)。 */
export function knightStepDustEventsFor(before: Boss, after: Boss, nowMs: number, opt: KnightStepDustOption = option): KnightStepDustEvent[] {
  if (opt === 'a' || after.id !== 'vigil-knight' || after.state !== 'idle') return []
  if (getKnightMove() !== 'step' || getKnightWalkPose() !== 'open') return []
  const b = before.knightGait?.step, a = after.knightGait?.step
  const f = after.facing === 'right' ? 1 : -1
  const feet = knightWalkFootPx()
  const floorY = after.y + 12
  const out: KnightStepDustEvent[] = []
  const started = !!a && (!b || b.fromX !== a.fromX || b.toX !== a.toX)
  const ended = !!b && (!a || b.fromX !== a.fromX || b.toX !== a.toX)
  if (ended && b && (opt === 'b' || opt === 'c')) {
    const travel: 1 | -1 = b.toX >= b.fromX ? 1 : -1
    const backward = travel !== f
    // 運び終えた = 揃えかけの構えで立つ。B = 進む側の足 (着いた足) / C = 寄せた側の足。
    const lead = backward ? feet.walk_passing.back : feet.walk_passing.front
    const trail = backward ? feet.walk_passing.front : feet.walk_passing.back
    const x = after.x + f * (opt === 'b' ? lead : trail)
    out.push(makeEvent(opt, x, floorY, travel, nowMs))
  }
  if (started && a && opt === 'd') {
    const travel: 1 | -1 = a.toX >= a.fromX ? 1 : -1
    const backward = travel !== f
    // 出だし = 広い構えの絵に変わった瞬間。後ろ側の足 (蹴る足) の所。絵のずれ (描画だけのずれ) も足す。
    const push = backward ? feet.walk_reach.front : feet.walk_reach.back
    // 開いて出る歩きは、広い構えの絵が1歩の間ずっと前へ滑る (蹴った足も一緒に動く)。砂は床に残るので、
    //   蹴った瞬間の踵の所に置くと、濃くなる頃 (約 60ms) には踵から離れて見える。濃くなる頃の踵の所に置く。
    //   さらに見本の砂は踵のすぐ後ろから出る (材料の切り出しは踵の 27px 後ろから) ので、その分 (ゲームで約 10px) 前へ寄せる。
    const KICK_AT = 0.28   // smoothstep(60 / 170)
    const x = after.x + knightGaitDrawOffsetPx(after) + f * push + (a.toX - a.fromX) * KICK_AT + travel * 10
    out.push(makeEvent(opt, x, floorY, travel, nowMs))
  }
  return out
}

/** 歩きの砂の置き場。game.ts が1 tick ごとに emit、render.ts が毎フレーム描く (10-01d から配布版も)。 */
export class KnightStepDust {
  events: KnightStepDustEvent[] = []
  emit(before: Boss, after: Boss, nowMs: number): void {
    if (option === 'a') return
    preloadKnightStepDust()
    this.events.push(...knightStepDustEventsFor(before, after, nowMs))
  }
  clear(): void { this.events = [] }
  render(ctx: CanvasRenderingContext2D, now: number): void {
    if (!this.events.length) return
    this.events = this.events.filter((e) => {
      const age = now - e.born
      return age >= -1000 && age <= PLAN[e.opt].end + 200
    })
    ctx.save()
    for (const e of this.events) drawEvent(ctx, e, now)
    ctx.restore()
  }
}

export const knightStepDust = /*#__PURE__*/ new KnightStepDust()

// URL の読み取りは最後 (上の let が初期化された後)。配布版はこの箱ごと消える = 常に c (寄せ足の擦り・10-01d)。
if (import.meta.env.DEV && typeof window !== 'undefined') {
  setKnightStepDust(new URLSearchParams(window.location.search).get('knightStepDust'))
  preloadKnightStepDust()
  // 撮影道具 (scripts/knight_fx_motion_shots.mjs) が、ゲームが実際に使っている置き場を読む覗き窓。
  ;(globalThis as { __knightStepDust?: KnightStepDust }).__knightStepDust = knightStepDust
}
